/**
 * 路由守卫工具类
 * 用于处理路由前置守卫的逻辑
 */

import store from '@/store'

/**
 * 检查用户是否已登录
 * @returns {boolean} 是否已登录
 */
export function isUserLoggedIn() {
    return store.getters.isLogin
}

/**
 * 检查用户是否已绑定手机号
 * @returns {boolean} 是否已绑定手机号
 */
export function isUserMobile() {
    let userInfo = store.getters.userInfo
    if (userInfo && !userInfo.mobile) {
        return false
    } else {
        return true
    }
}

/**
 * 获取用户token
 * @returns {string} 用户token
 */
export function getUserToken() {
    return store.getters.token
}

/**
 * 验证token有效性
 * @returns {Promise<boolean>} token是否有效
 */
export async function validateToken() {
    try {
        const token = getUserToken()
        if (!token) {
            return false
        }

        // 尝试获取用户信息来验证token
        await store.dispatch('GET_USER_INFO')
        return true
    } catch (error) {
        console.warn('Token验证失败:', error)
        return false
    }
}

/**
 * 清除用户登录状态
 */
export function clearUserLoginState() {
    store.commit('SET_LOGIN', false)
    store.commit('SET_TOKEN', '')
    store.commit('SET_USERINFO', {})
    store.commit('SET_ROLE', '')
    store.commit('SET_IS_CHECK', 0)

    // 清除localStorage中的相关数据
    localStorage.removeItem('isLogin')
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('role')
    localStorage.removeItem('is_check')
}

/**
 * 处理需要登录验证的路由
 * @param {Object} to 目标路由
 * @param {Object} from 来源路由
 * @param {Function} next 路由跳转函数
 */
export async function handleAuthRoute(to, from, next) {
    const isLogin = isUserLoggedIn()
    const token = getUserToken()

    // 如果用户已登录
    if (isLogin) {
        // 检查是否访问包含"user"的页面
        const isUserPage = to.path.includes('/user')
        
        // 检查是否在绑定手机号页面
        const isBindPhonePage = to.path === '/bindPhone'

        // 检查手机号绑定状态
        const bindPhone = isUserMobile()

        // 如果访问user页面但未绑定手机号，重定向到绑定页面
        if (isUserPage && !bindPhone && !isBindPhonePage) {
            next({
                path: "/bindPhone",
                query: {redirect: to.fullPath}
            })
            return
        }

        // 其他情况允许访问
        next()
        return
    }

    // 如果本地存储显示已登录但store中没有登录状态，尝试恢复登录状态
    if (!isLogin && token) {
        try {
            const isValid = await validateToken()
            if (isValid) {
                // 验证通过后检查手机号绑定状态
                const bindPhone = isUserMobile()
                const isUserPage = to.path.includes('/user')
                const isBindPhonePage = to.path === '/bindPhone'

                // 如果访问user页面但未绑定手机号，重定向到绑定页面
                if (isUserPage && !bindPhone && !isBindPhonePage) {
                    next({
                        path: "/bindPhone",
                        query: {redirect: to.fullPath}
                    })
                    return
                }
                next()
                return
            }
        } catch (error) {
            console.warn('Token验证失败，跳转到登录页:', error)
        }

        // 清除无效的登录状态
        clearUserLoginState()
    }

    // 未登录且访问的是需要登录的页面，跳转到登录页，并记录原目标路径
    // 但排除首页和绑定手机号页面，允许未登录用户访问
    if (to.path !== '/bindPhone' && to.path !== '/' && to.path !== '') {
        next({
            path: "/login",
            query: {redirect: to.fullPath}
        })
    } else {
        // 允许访问首页和绑定手机号页面
        next()
    }
}

/**
 * 检查用户权限
 * @param {string} requiredRole 需要的角色
 * @returns {boolean} 是否有权限
 */
export function checkUserPermission(requiredRole) {
    const userRole = store.getters.role
    return userRole === requiredRole
}

/**
 * 检查用户是否为VIP
 * @returns {boolean} 是否为VIP
 */
export function isUserVIP() {
    return store.getters.is_vip
}

/**
 * 检查用户审核状态
 * @returns {number} 审核状态
 */
export function getUserCheckStatus() {
    return store.getters.is_check
}
